import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Container, Row, Col, Card, CardBody, Nav, NavItem, NavLink, Modal, ModalHeader, ModalBody } from "reactstrap";
import { getCommercialName } from "../constants/commercialNames";
import { getAuthUser, getAuthToken } from "../hooks/useAuthToken";
import { apiV2PostConfig } from "../helpers/api_helper";

const CategoryServices = () => {
    const { categoryId } = useParams<{ categoryId: string }>();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState<boolean>(true);
    const [categoryName, setCategoryName] = useState<string>("");
    const [categoryNameCommercial, setCategoryNameCommercial] = useState<string>("");
    const [services, setServices] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<string>("info");
    
    const [sdaModalOpen, setSdaModalOpen] = useState(false);
    const [selectedSdaNumbers, setSelectedSdaNumbers] = useState<string[]>([]);
    const [selectedServiceName, setSelectedServiceName] = useState<string>("");

    const toggleSdaModal = () => setSdaModalOpen(!sdaModalOpen);

    const openSdaModal = (numbers: string[], serviceName: string) => {
        setSelectedSdaNumbers(numbers);
        setSelectedServiceName(serviceName);
        setSdaModalOpen(true);
    };

    const isMobileCategory = () => {
        return categoryNameCommercial.toLowerCase().includes('mobile') || 
               categoryNameCommercial.toLowerCase().includes('téléphonie mobile');
    };

    const checkIsVoiceCategory = (categoryName: string) => {
        const name = categoryName.toLowerCase();
        return name.includes('voix') || 
               name.includes('fixe') ||
               name.includes('téléphonie fixe');
    };

    const groupVoiceServices = (servicesList: any[], categoryName: string) => {
        const isVoice = checkIsVoiceCategory(categoryName);
        if (!isVoice) {
            return servicesList;
        }

        const grouped: any[] = [];
        const processedIds = new Set<string>();

        servicesList.forEach((service) => {
            if (processedIds.has(service.contract_line_id)) {
                return;
            }

            const similarServices = servicesList.filter(
                (s) =>
                    s.product_id === service.product_id &&
                    s.unit_price_ht === service.unit_price_ht &&
                    !processedIds.has(s.contract_line_id)
            );

            similarServices.forEach((s) => {
                processedIds.add(s.contract_line_id);
            });

            const sdaNumbers = similarServices
                .map((s) => {
                    if (s.extrafields) {
                        const sdaKey = Object.keys(s.extrafields).find(
                            (key) =>
                                key.toLowerCase().includes('sda') ||
                                key.toLowerCase().includes('numéro') ||
                                key.toLowerCase().includes('numero')
                        );
                        if (sdaKey) {
                            return s.extrafields[sdaKey];
                        }
                    }
                    return null;
                })
                .filter((num) => num !== null);

            const totalQuantity = similarServices.reduce(
                (sum, s) => sum + parseInt(s.quantity || 0),
                0
            );

            const totalHT = similarServices.reduce(
                (sum, s) => sum + parseFloat(s.line_total_ht || 0),
                0
            );

            grouped.push({
                ...service,
                quantity: totalQuantity.toString(),
                line_total_ht: totalHT.toFixed(2),
                sda_numbers: sdaNumbers,
                is_grouped: similarServices.length > 1,
                grouped_count: similarServices.length,
            });
        });

        return grouped;
    };

    const fetchCategoryName = async (): Promise<string> => {
        try {
            const authUser = getAuthUser();
            if (!authUser?.token) return "";
            const entity = authUser.entity || 1;

            const response: any = await axios.post(
                "/apiV2/",
                { action: "getCategories", entity },
                apiV2PostConfig()
            );

            const categoriesData = response.data || response;
            const category = categoriesData.find((cat: any) => cat.id === categoryId);

            if (category) {
                const commercialName = getCommercialName(category.label);
                setCategoryName(category.label);
                setCategoryNameCommercial(commercialName);
                return commercialName;
            }

            return "";
        } catch (error) {
            console.error("Erreur lors du chargement de la catégorie:", error);
            return "";
        }
    };

    const fetchCategoryServices = async (categoryName: string) => {
        try {
            const token = getAuthToken();
            if (!token) return;

            const response: any = await axios.post(
                "/apiV2/",
                {
                    action: "getServices",
                    categories: [parseInt(categoryId!)],
                    entity: 1,
                },
                apiV2PostConfig()
            );

            let servicesData = [];

            if (response.data?.data && Array.isArray(response.data.data)) {
                servicesData = response.data.data;
            } else if (Array.isArray(response.data)) {
                servicesData = response.data;
            }

            const processedServices = groupVoiceServices(servicesData || [], categoryName);
            setServices(processedServices);

        } catch (error) {
            console.error("Erreur lors du chargement des services:", error);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            const categoryName = await fetchCategoryName();
            await fetchCategoryServices(categoryName);
            setLoading(false);
        };

        if (categoryId) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId]);

    const getCategoryIcon = () => {
        const name = categoryNameCommercial.toLowerCase();
        if (name.includes('internet') || name.includes('connectivité') || name.includes('lien')) return 'ri-global-line';
        if (name.includes('mobile')) return 'ri-smartphone-line';
        if (name.includes('voix') || name.includes('fixe')) return 'ri-phone-line';
        if (name.includes('cybersécurité') || name.includes('sécurité')) return 'ri-shield-check-line';
        if (name.includes('équipement')) return 'ri-router-line';
        if (name.includes('matériel')) return 'ri-tools-line';
        return 'ri-service-line';
    };

    type DataConsumption = {
        total: number;
        used: number;
        remaining: number;
        percentage: number;
        unit: string;
    };

    type CallsConsumption = {
        total: number;
        used: number;
        remaining: number;
        unit: string;
    };

    type SmsConsumption = {
        total: string;
        sent: number;
        unit: string;
    };

    const getDataConsumption = (service: any): DataConsumption | null => {
        if (!service.infomobile) return null;

        const { dataligne, restant, pourcentage } = service.infomobile;
        
        const totalGo = dataligne / 1024;
        const restantGo = restant / 1024;
        const usedGo = totalGo - restantGo;

        return {
            total: parseFloat(totalGo.toFixed(2)),
            used: parseFloat(usedGo.toFixed(2)),
            remaining: parseFloat(restantGo.toFixed(2)),
            percentage: parseFloat(pourcentage.toFixed(2)),
            unit: 'Go'
        };
    };

    const getMockCallsConsumption = (): CallsConsumption => {
        return { total: 120, used: 45, remaining: 75, unit: 'min' };
    };

    const getMockSmsConsumption = (): SmsConsumption => {
        return { total: 'Illimités', sent: 124, unit: 'SMS/MMS' };
    };

    const renderServiceCard = (service: any, index: number) => {
        if (!isMobileCategory()) {
            return renderDefaultCard(service, index);
        }

        switch (activeTab) {
            case 'data':
                return renderDataCard(service, index);
            case 'communication':
                return renderCommunicationCard(service, index);
            default:
                return renderDefaultCard(service, index);
        }
    };

    const renderSdaBadges = (service: any) => {
        if (!service.sda_numbers || service.sda_numbers.length === 0) {
            return service.extrafields && Object.keys(service.extrafields).length > 0 && Object.entries(service.extrafields)
                .filter(([key]) => key.toLowerCase() !== 'contrat')
                .map(([key, value]: [string, any]) => {
                    const isPhoneNumber = key.toLowerCase().includes('sda') || 
                                         key.toLowerCase().includes('numéro') || 
                                         key.toLowerCase().includes('numero') ||
                                         /^\d{10}$/.test(String(value));
                    
                    return (
                        <div className="iptrend-extrafield-badge" key={key}>
                            <i className={isPhoneNumber ? "ri-phone-line me-1" : "ri-information-line me-1"}></i>
                            {value}
                        </div>
                    );
                });
        }

        const displayCount = 2;
        const visibleNumbers = service.sda_numbers.slice(0, displayCount);
        const remainingCount = service.sda_numbers.length - displayCount;

        return (
            <>
                {visibleNumbers.map((number: string, idx: number) => (
                    <div className="iptrend-extrafield-badge" key={idx}>
                        <i className="ri-phone-line me-1"></i>
                        {number}
                    </div>
                ))}
                {remainingCount > 0 && (
                    <div 
                        className="iptrend-extrafield-badge iptrend-more-badge" 
                        onClick={() => openSdaModal(service.sda_numbers, service.product_label || service.product_ref)}
                        style={{ cursor: 'pointer' }}
                    >
                        <i className="ri-more-2-fill me-1"></i>
                        +{remainingCount} autre{remainingCount > 1 ? 's' : ''}
                    </div>
                )}
            </>
        );
    };

    const renderDefaultCard = (service: any, index: number) => (
        <Col xl={4} lg={6} key={index}>
            <Card className="iptrend-service-card border-0 shadow-sm h-100">
                <CardBody className="p-0">
                    <div className="iptrend-card-header">
                        <div className="d-flex align-items-center">
                            <div className="iptrend-service-icon me-3">
                                <i className={getCategoryIcon()}></i>
                            </div>
                            <div className="flex-grow-1">
                                <h5 className="text-white mb-2 fw-bold">
                                    {service.product_label || service.product_ref}
                                </h5>
                                <div className="d-flex flex-wrap gap-2">
                                    <div className="iptrend-ref-badge">
                                        <i className="ri-price-tag-3-line me-1"></i>
                                        {service.product_ref}
                                    </div>
                                    
                                    {renderSdaBadges(service)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-4">
                        <div className="iptrend-detail-item">
                            <div className="detail-label">
                                <i className="ri-file-list-3-line me-2"></i>
                                Contrat
                            </div>
                            <div className="detail-value">{service.contract_ref}</div>
                        </div>
                        
                        <div className="iptrend-detail-item">
                            <div className="detail-label">
                                <i className="ri-stack-line me-2"></i>
                                Quantité
                            </div>
                            <div className="detail-value">
                                <span className="iptrend-quantity-badge">{service.quantity}</span>
                            </div>
                        </div>

                        {service.unit_price_ht && (
                            <div className="iptrend-detail-item">
                                <div className="detail-label">
                                    <i className="ri-money-euro-circle-line me-2"></i>
                                    Prix unitaire HT
                                </div>
                                <div className="detail-value">{service.unit_price_ht} €</div>
                            </div>
                        )}

                        {service.line_total_ht && (
                            <div className="iptrend-detail-item iptrend-total">
                                <div className="detail-label fw-bold">
                                    <i className="ri-price-tag-3-fill me-2"></i>
                                    Total HT
                                </div>
                                <div className="detail-value fw-bold iptrend-price">
                                    {service.line_total_ht} €
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="iptrend-card-footer">
                        <div className="iptrend-status-badge">
                            <i className="ri-checkbox-circle-fill me-2"></i>
                            Service actif
                        </div>
                    </div>
                </CardBody>
            </Card>
        </Col>
    );

    const renderDataCard = (service: any, index: number) => {
        const consumption = getDataConsumption(service);
        
        if (!consumption) {
            return (
                <Col xl={4} lg={6} key={index}>
                    <Card className="iptrend-service-card border-0 shadow-sm h-100">
                        <CardBody className="p-0">
                            <div className="iptrend-card-header">
                                <div className="d-flex align-items-center">
                                    <div className="iptrend-service-icon me-3">
                                        <i className="ri-global-line"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <h5 className="text-white mb-2 fw-bold">
                                            {service.product_label || service.product_ref}
                                        </h5>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4">
                                <div className="text-center text-muted">
                                    <i className="ri-information-line" style={{ fontSize: '48px' }}></i>
                                    <p className="mt-3">Données de consommation non disponibles</p>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            );
        }

        return (
            <Col xl={4} lg={6} key={index}>
                <Card className="iptrend-service-card border-0 shadow-sm h-100">
                    <CardBody className="p-0">
                        <div className="iptrend-card-header">
                            <div className="d-flex align-items-center">
                                <div className="iptrend-service-icon me-3">
                                    <i className="ri-global-line"></i>
                                </div>
                                <div className="flex-grow-1">
                                    <h5 className="text-white mb-2 fw-bold">
                                        {service.product_label || service.product_ref}
                                    </h5>
                                    <div className="iptrend-ref-badge">
                                        <i className="ri-database-2-line me-1"></i>
                                        Forfait {consumption.total}{consumption.unit}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-4">
                            <div className="consumption-stats mb-4">
                                <div className="d-flex justify-content-between mb-3">
                                    <div className="stat-item">
                                        <div className="stat-label">Utilisé</div>
                                        <div className="stat-value text-danger">{consumption.used} {consumption.unit}</div>
                                    </div>
                                    <div className="stat-item text-end">
                                        <div className="stat-label">Restant</div>
                                        <div className="stat-value text-success">{consumption.remaining} {consumption.unit}</div>
                                    </div>
                                </div>

                                <div className="progress-bar-container">
                                    <div className="progress-bar-bg">
                                        <div 
                                            className="progress-bar-fill" 
                                            style={{ width: `${100 - consumption.percentage}%` }}
                                        ></div>
                                    </div>
                                    <div className="progress-percentage">{(100 - consumption.percentage).toFixed(0)}% utilisé</div>
                                </div>
                            </div>

                            <div className="iptrend-detail-item">
                                <div className="detail-label">
                                    <i className="ri-file-list-3-line me-2"></i>
                                    Contrat
                                </div>
                                <div className="detail-value">{service.contract_ref}</div>
                            </div>
                        </div>

                        <div className="iptrend-card-footer">
                            <div className="iptrend-status-badge">
                                <i className="ri-wifi-line me-2"></i>
                                Data actif
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        );
    };

    const renderCommunicationCard = (service: any, index: number) => {
        const callsConsumption = getMockCallsConsumption();
        const smsConsumption = getMockSmsConsumption();
        const callsPercentage = (callsConsumption.used / callsConsumption.total) * 100;

        return (
            <Col xl={4} lg={6} key={index}>
                <Card className="iptrend-service-card border-0 shadow-sm h-100">
                    <CardBody className="p-0">
                        <div className="iptrend-card-header">
                            <div className="d-flex align-items-center">
                                <div className="iptrend-service-icon me-3">
                                    <i className="ri-chat-voice-line"></i>
                                </div>
                                <div className="flex-grow-1">
                                    <h5 className="text-white mb-2 fw-bold">
                                        {service.product_label || service.product_ref}
                                    </h5>
                                    <div className="d-flex flex-wrap gap-2">
                                        <div className="iptrend-ref-badge">
                                            <i className="ri-phone-line me-1"></i>
                                            {callsConsumption.total} {callsConsumption.unit}
                                        </div>
                                        <div className="iptrend-ref-badge">
                                            <i className="ri-message-3-line me-1"></i>
                                            SMS {smsConsumption.total}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-3">
                            <div className="consumption-section-compact mb-3">
                                <div className="consumption-section-title-compact mb-2">
                                    <i className="ri-phone-fill me-2"></i>
                                    Appels
                                </div>
                                <div className="d-flex justify-content-between mb-2 stat-row-compact">
                                    <div>
                                        <span className="stat-label-compact">Consommé</span>
                                        <span className="stat-value-compact text-danger ms-2">{callsConsumption.used} {callsConsumption.unit}</span>
                                    </div>
                                    <div>
                                        <span className="stat-label-compact">Restant</span>
                                        <span className="stat-value-compact text-success ms-2">{callsConsumption.remaining} {callsConsumption.unit}</span>
                                    </div>
                                </div>
                                <div className="progress-bar-bg-compact">
                                    <div 
                                        className="progress-bar-fill progress-bar-calls" 
                                        style={{ width: `${callsPercentage}%` }}
                                    ></div>
                                </div>
                                <div className="progress-percentage-compact">{callsPercentage.toFixed(0)}% utilisé</div>
                            </div>

                            <div className="consumption-section-compact mb-3">
                                <div className="consumption-section-title-compact mb-2">
                                    <i className="ri-message-3-fill me-2"></i>
                                    SMS/MMS
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="sms-sent-compact">
                                        <span className="sms-count-number-compact">{smsConsumption.sent}</span>
                                        <span className="sms-label-compact"> envoyés</span>
                                    </div>
                                    <div className="unlimited-badge-compact">
                                        <i className="ri-infinity-line me-1"></i>
                                        Illimités
                                    </div>
                                </div>
                            </div>

                            <div className="iptrend-detail-item-compact">
                                <div className="detail-label">
                                    <i className="ri-file-list-3-line me-2"></i>
                                    Contrat
                                </div>
                                <div className="detail-value">{service.contract_ref}</div>
                            </div>
                        </div>

                        <div className="iptrend-card-footer">
                            <div className="iptrend-status-badge">
                                <i className="ri-chat-check-line me-2"></i>
                                Communication active
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        );
    };

    if (loading) {
        return (
            <div className="page-content iptrend-page">
                <Container fluid>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <div className="spinner-border iptrend-spinner">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="text-muted mt-3 fw-medium">Chargement de vos services...</p>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content iptrend-page">
            <Container fluid>
                <div className="iptrend-header-gradient mb-4">
                    <div className="d-flex align-items-center">
                        <button 
                            className="iptrend-btn-back me-4"
                            onClick={() => navigate(-1)}
                        >
                            <i className="ri-arrow-left-line"></i>
                        </button>
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center">
                                <div className="iptrend-category-icon me-4">
                                    <i className={getCategoryIcon()}></i>
                                </div>
                                <div>
                                    <h2 className="text-white mb-2 fw-bold">
                                        {categoryNameCommercial}
                                    </h2>
                                    <p className="text-white-50 mb-0 fs-5">
                                        <i className="ri-checkbox-circle-fill me-2"></i>
                                        {services.length} service{services.length > 1 ? 's' : ''} actif{services.length > 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {isMobileCategory() && services.length > 0 && (
                    <Card className="mb-4 border-0 shadow-sm">
                        <CardBody className="p-0">
                            <Nav tabs className="iptrend-tabs">
                                <NavItem>
                                    <NavLink
                                        className={activeTab === 'info' ? 'active' : ''}
                                        onClick={() => setActiveTab('info')}
                                    >
                                        <i className="ri-information-line me-2"></i>
                                        Informations
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        className={activeTab === 'data' ? 'active' : ''}
                                        onClick={() => setActiveTab('data')}
                                    >
                                        <i className="ri-global-line me-2"></i>
                                        Internet
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        className={activeTab === 'communication' ? 'active' : ''}
                                        onClick={() => setActiveTab('communication')}
                                    >
                                        <i className="ri-chat-voice-line me-2"></i>
                                        Appels & SMS
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </CardBody>
                    </Card>
                )}

                {services.length === 0 ? (
                    <div className="text-center py-5">
                        <Card className="iptrend-card border-0 shadow" style={{ maxWidth: '600px', margin: '0 auto' }}>
                            <CardBody className="p-5">
                                <div className="iptrend-empty-icon mb-4">
                                    <i className={getCategoryIcon()}></i>
                                </div>
                                <h3 className="mb-3 fw-bold">Aucun service disponible</h3>
                                <p className="text-muted mb-4 fs-5">
                                    Vous n'avez pas encore souscrit de service dans la catégorie "{categoryNameCommercial}"
                                </p>
                                <button className="iptrend-btn-primary" onClick={() => navigate('/services')}>
                                    <i className="ri-arrow-left-line me-2"></i>
                                    Retour aux services
                                </button>
                            </CardBody>
                        </Card>
                    </div>
                ) : (
                    <Row className="g-4">
                        {services.map((service, index) => renderServiceCard(service, index))}
                    </Row>
                )}
            </Container>

            <Modal isOpen={sdaModalOpen} toggle={toggleSdaModal} size="md" className="iptrend-sda-modal" centered>
                <ModalHeader toggle={toggleSdaModal} className="iptrend-modal-header border-0">
                    <div className="d-flex align-items-center">
                        <div className="iptrend-modal-icon me-3">
                            <i className="ri-phone-line"></i>
                        </div>
                        <div>
                            <h5 className="mb-1">{selectedServiceName}</h5>
                            <small className="text-white-50">Liste des numéros SDA</small>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="p-4">
                    <div className="sda-list">
                        {selectedSdaNumbers.map((number, idx) => (
                            <div key={idx} className="sda-list-item">
                                <div className="sda-number-icon">
                                    <i className="ri-phone-fill"></i>
                                </div>
                                <div className="sda-number-text">{number}</div>
                                <div className="sda-number-badge">SDA {idx + 1}</div>
                            </div>
                        ))}
                    </div>
                </ModalBody>
            </Modal>

            <style>{`
                :root {
                    --iptrend-blue: #0066A1;
                    --iptrend-teal: #00A389;
                    --iptrend-green: #7AC943;
                    --iptrend-dark: #1a3d5c;
                }

                .iptrend-page {
                    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                    min-height: 100vh;
                }

                .iptrend-header-gradient {
                    background: linear-gradient(135deg, var(--iptrend-blue) 0%, var(--iptrend-teal) 50%, var(--iptrend-green) 100%);
                    padding: 2rem;
                    border-radius: 20px;
                    box-shadow: 0 10px 30px rgba(0, 102, 161, 0.3);
                    position: relative;
                    overflow: hidden;
                }

                .iptrend-header-gradient::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: url('data:image/svg+xml,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)" /></svg>');
                    opacity: 0.3;
                }

                .iptrend-btn-back {
                    width: 50px;
                    height: 50px;
                    border-radius: 12px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    background: rgba(255, 255, 255, 0.15);
                    backdrop-filter: blur(10px);
                    color: white;
                    font-size: 22px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    z-index: 1;
                }

                .iptrend-btn-back:hover {
                    background: white;
                    color: var(--iptrend-teal);
                    transform: translateX(-5px);
                    border-color: white;
                }

                .iptrend-category-icon {
                    width: 70px;
                    height: 70px;
                    border-radius: 18px;
                    background: rgba(255, 255, 255, 0.95);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 32px;
                    color: var(--iptrend-teal);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                    z-index: 1;
                }

                /* 🔥 Badge "+X autres" cliquable */
                .iptrend-more-badge {
                    background: linear-gradient(135deg, var(--iptrend-blue) 0%, var(--iptrend-teal) 100%) !important;
                    transition: all 0.3s ease;
                }

                .iptrend-more-badge:hover {
                    transform: scale(1.05);
                    box-shadow: 0 4px 12px rgba(0, 102, 161, 0.4) !important;
                }

                /* 🔥 MODAL SDA */
                .iptrend-sda-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    overflow: hidden;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                }

                .iptrend-modal-header {
                    background: linear-gradient(135deg, var(--iptrend-blue) 0%, var(--iptrend-teal) 100%);
                    color: white;
                    padding: 1.5rem 2rem;
                    border: none;
                }

                .iptrend-modal-header h5 {
                    color: white;
                    font-weight: 700;
                    margin: 0;
                    font-size: 1.1rem;
                }

                .iptrend-modal-icon {
                    width: 50px;
                    height: 50px;
                    border-radius: 12px;
                    background: rgba(255, 255, 255, 0.2);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                }

                .iptrend-modal-header .btn-close {
                    filter: brightness(0) invert(1);
                    opacity: 0.8;
                }

                .iptrend-modal-header .btn-close:hover {
                    opacity: 1;
                    transform: rotate(90deg);
                    transition: all 0.3s ease;
                }

                /* Liste des numéros SDA */
                .sda-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .sda-list-item {
                    display: flex;
                    align-items: center;
                    padding: 1rem 1.25rem;
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                    border-radius: 12px;
                    transition: all 0.3s ease;
                    border: 2px solid transparent;
                }

                .sda-list-item:hover {
                    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
                    border-color: var(--iptrend-teal);
                    transform: translateX(5px);
                }

                .sda-number-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: linear-gradient(135deg, var(--iptrend-teal) 0%, var(--iptrend-green) 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 18px;
                    margin-right: 1rem;
                    flex-shrink: 0;
                }

                .sda-number-text {
                    flex-grow: 1;
                    font-weight: 600;
                    font-size: 1rem;
                    color: var(--iptrend-dark);
                }

                .sda-number-badge {
                    background: white;
                    color: var(--iptrend-teal);
                    padding: 0.35rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 700;
                    border: 2px solid var(--iptrend-teal);
                }

                /* ONGLETS */
                .iptrend-tabs {
                    border-bottom: none;
                    background: #f8f9fa;
                    border-radius: 16px 16px 0 0;
                    padding: 0.5rem 1rem 0;
                    display: flex;
                    flex-wrap: wrap;
                }

                .iptrend-tabs .nav-item {
                    margin: 0 0.25rem 0.5rem;
                }

                .iptrend-tabs .nav-link {
                    border: none;
                    background: transparent;
                    color: #6c757d;
                    font-weight: 600;
                    font-size: 0.9rem;
                    padding: 0.75rem 1.5rem;
                    border-radius: 12px;
                    transition: all 0.3s ease;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                }

                .iptrend-tabs .nav-link:hover {
                    background: rgba(0, 163, 137, 0.1);
                    color: var(--iptrend-teal);
                }

                .iptrend-tabs .nav-link.active {
                    background: linear-gradient(135deg, var(--iptrend-teal) 0%, var(--iptrend-green) 100%);
                    color: white;
                    box-shadow: 0 4px 12px rgba(0, 163, 137, 0.3);
                }

                /* STATISTIQUES DE CONSOMMATION */
                .consumption-stats {
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                    padding: 1.5rem;
                    border-radius: 12px;
                }

                /* SECTIONS DE CONSOMMATION COMPACT */
                .consumption-section-compact {
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                    border-radius: 8px;
                    padding: 0.75rem;
                }

                .consumption-section-title-compact {
                    color: var(--iptrend-dark);
                    font-weight: 700;
                    font-size: 0.85rem;
                    display: flex;
                    align-items: center;
                }

                .consumption-section-title-compact i {
                    color: var(--iptrend-teal);
                    font-size: 1rem;
                }

                .stat-row-compact {
                    font-size: 0.85rem;
                }

                .stat-label-compact {
                    color: #6c757d;
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    font-weight: 600;
                }

                .stat-value-compact {
                    font-size: 1rem;
                    font-weight: 700;
                }

                .progress-bar-bg-compact {
                    width: 100%;
                    height: 8px;
                    background: #e9ecef;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
                    margin-top: 0.5rem;
                }

                .progress-percentage-compact {
                    text-align: center;
                    font-size: 0.7rem;
                    color: #6c757d;
                    font-weight: 600;
                    margin-top: 0.35rem;
                }

                .sms-sent-compact {
                    display: flex;
                    align-items: baseline;
                    gap: 0.25rem;
                }

                .sms-count-number-compact {
                    font-size: 1.75rem;
                    font-weight: 700;
                    background: linear-gradient(135deg, var(--iptrend-teal) 0%, var(--iptrend-green) 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .sms-label-compact {
                    font-size: 0.75rem;
                    color: #6c757d;
                    font-weight: 600;
                }

                .unlimited-badge-compact {
                    background: linear-gradient(135deg, var(--iptrend-teal) 0%, var(--iptrend-green) 100%);
                    color: white;
                    padding: 0.35rem 0.75rem;
                    border-radius: 15px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    box-shadow: 0 2px 8px rgba(0, 163, 137, 0.25);
                }

                .iptrend-detail-item-compact {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.6rem 0;
                    border-top: 1px dashed #e9ecef;
                }

                .stat-item {
                    flex: 1;
                }

                .stat-label {
                    font-size: 0.75rem;
                    color: #6c757d;
                    text-transform: uppercase;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    margin-bottom: 0.5rem;
                }

                .stat-value {
                    font-size: 1.5rem;
                    font-weight: 700;
                }

                /* BARRE DE PROGRESSION */
                .progress-bar-container {
                    margin-top: 1rem;
                }

                .progress-bar-bg {
                    width: 100%;
                    height: 12px;
                    background: #e9ecef;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
                }

                .progress-bar-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #dc3545 0%, #ff6b6b 100%);
                    border-radius: 20px;
                    transition: width 0.5s ease;
                }

                .progress-bar-calls {
                    background: linear-gradient(90deg, #0066A1 0%, #00A389 100%);
                }

                .progress-percentage {
                    text-align: center;
                    font-size: 0.75rem;
                    color: #6c757d;
                    font-weight: 600;
                    margin-top: 0.5rem;
                }

                .iptrend-spinner {
                    width: 3rem;
                    height: 3rem;
                    border-width: 0.3rem;
                    border-color: var(--iptrend-teal);
                    border-right-color: transparent;
                }

                .iptrend-service-card {
                    border-radius: 16px;
                    overflow: hidden;
                    transition: all 0.4s ease;
                    background: white;
                }

                .iptrend-service-card:hover {
                    transform: translateY(-10px);
                    box-shadow: 0 15px 40px rgba(0, 102, 161, 0.2) !important;
                }

                .iptrend-card-header {
                    background: linear-gradient(135deg, var(--iptrend-blue) 0%, var(--iptrend-teal) 100%);
                    padding: 1.5rem;
                    position: relative;
                    overflow: hidden;
                }

                .iptrend-card-header::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    right: -10%;
                    width: 200px;
                    height: 200px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 50%;
                }

                .iptrend-service-icon {
                    width: 50px;
                    height: 50px;
                    border-radius: 12px;
                    background: rgba(255, 255, 255, 0.2);
                    backdrop-filter: blur(10px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    color: white;
                    flex-shrink: 0;
                }

                .iptrend-ref-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 4px 12px;
                    background: rgba(255, 255, 255, 0.25);
                    backdrop-filter: blur(10px);
                    color: white;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }

                .iptrend-extrafield-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 4px 12px;
                    background: #6EC549;
                    backdrop-filter: blur(10px);
                    color: white;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    box-shadow: 0 2px 6px rgba(110, 197, 73, 0.4);
                }

                .iptrend-detail-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.875rem 0;
                    border-bottom: 1px dashed #e9ecef;
                }

                .iptrend-detail-item:last-child {
                    border-bottom: none;
                }

                .detail-label {
                    color: #6c757d;
                    font-size: 0.9rem;
                    display: flex;
                    align-items: center;
                }

                .detail-label i {
                    color: var(--iptrend-teal);
                }

                .detail-value {
                    color: var(--iptrend-dark);
                    font-weight: 500;
                    font-size: 0.9rem;
                }

                .iptrend-quantity-badge {
                    background: linear-gradient(135deg, var(--iptrend-teal) 0%, var(--iptrend-green) 100%);
                    color: white;
                    padding: 4px 14px;
                    border-radius: 20px;
                    font-size: 0.8rem;
                    font-weight: 600;
                    box-shadow: 0 2px 8px rgba(0, 163, 137, 0.3);
                }

                .iptrend-total {
                    padding-top: 1.25rem;
                    margin-top: 0.75rem;
                    border-top: 2px solid var(--iptrend-teal) !important;
                    border-bottom: none !important;
                }

                .iptrend-price {
                    color: var(--iptrend-teal);
                    font-size: 1.35rem;
                }

                .iptrend-card-footer {
                    background: linear-gradient(to right, #f8f9fa, #e9ecef);
                    padding: 1rem;
                    text-align: center;
                    border-top: 1px solid #e9ecef;
                }

                .iptrend-status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 8px 20px;
                    background: white;
                    color: var(--iptrend-green);
                    border-radius: 25px;
                    font-size: 0.875rem;
                    font-weight: 600;
                    box-shadow: 0 2px 8px rgba(122, 201, 67, 0.2);
                    border: 2px solid var(--iptrend-green);
                }

                .iptrend-empty-icon {
                    width: 120px;
                    height: 120px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, var(--iptrend-blue) 0%, var(--iptrend-teal) 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 56px;
                    color: white;
                    margin: 0 auto;
                    box-shadow: 0 10px 30px rgba(0, 102, 161, 0.3);
                }

                .iptrend-btn-primary {
                    background: linear-gradient(135deg, var(--iptrend-green) 0%, #5fb52a 100%);
                    color: white;
                    border: none;
                    padding: 14px 36px;
                    border-radius: 25px;
                    font-weight: 600;
                    font-size: 1rem;
                    display: inline-flex;
                    align-items: center;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 6px 20px rgba(122, 201, 67, 0.4);
                }

                .iptrend-btn-primary:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 25px rgba(122, 201, 67, 0.5);
                    color: white;
                }

                .iptrend-card {
                    border-radius: 20px;
                }

                @media (max-width: 768px) {
                    .iptrend-header-gradient {
                        padding: 1.5rem;
                    }

                    .iptrend-category-icon {
                        width: 60px;
                        height: 60px;
                        font-size: 28px;
                    }

                    .iptrend-header-gradient h2 {
                        font-size: 1.5rem;
                    }

                    .iptrend-tabs {
                        padding: 0.25rem 0.5rem 0;
                    }

                    .iptrend-tabs .nav-link {
                        font-size: 0.8rem;
                        padding: 0.5rem 1rem;
                    }

                    .stat-value {
                        font-size: 1.25rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default CategoryServices;